import { useCallback, useEffect, useRef } from 'react';
import type { RefCallback } from 'react';

interface AvailableViewportHeightOptions {
    bottomOffset?: number;
}

const availableViewportHeightProperty = '--available-viewport-height';

export function useAvailableViewportHeight<T extends HTMLElement>({
    bottomOffset = 16,
}: AvailableViewportHeightOptions = {}): RefCallback<T> {
    const elementRef = useRef<T | null>(null);
    const resizeObserverRef = useRef<ResizeObserver>();

    const updateAvailableHeight = useCallback(() => {
        if (typeof window === 'undefined') return;
        const element = elementRef.current;
        if (!element) return;

        if (element.getClientRects().length === 0) {
            element.style.removeProperty(availableViewportHeightProperty);
            return;
        }

        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const elementTop = element.getBoundingClientRect().top;
        const remainingHeight = viewportHeight - elementTop - bottomOffset;

        if (remainingHeight <= 0) {
            element.style.removeProperty(availableViewportHeightProperty);
            return;
        }

        const nextHeight = `${Math.floor(remainingHeight)}px`;
        if (element.style.getPropertyValue(availableViewportHeightProperty) !== nextHeight) {
            element.style.setProperty(availableViewportHeightProperty, nextHeight);
        }
    }, [bottomOffset]);

    const setElementRef = useCallback<RefCallback<T>>((element) => {
        resizeObserverRef.current?.disconnect();
        elementRef.current = element;

        if (!element) return;

        updateAvailableHeight();

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserverRef.current = new ResizeObserver(updateAvailableHeight);
            resizeObserverRef.current.observe(element);
        }
    }, [updateAvailableHeight]);

    useEffect(() => {
        updateAvailableHeight();

        const scrollOptions: AddEventListenerOptions = { passive: true };
        const capturedScrollOptions: AddEventListenerOptions = {
            capture: true,
            passive: true,
        };

        window.addEventListener('resize', updateAvailableHeight);
        window.addEventListener('scroll', updateAvailableHeight, scrollOptions);
        document.addEventListener('scroll', updateAvailableHeight, capturedScrollOptions);

        const visualViewport = window.visualViewport;
        visualViewport?.addEventListener('resize', updateAvailableHeight, scrollOptions);
        visualViewport?.addEventListener('scroll', updateAvailableHeight, scrollOptions);

        return () => {
            window.removeEventListener('resize', updateAvailableHeight);
            window.removeEventListener('scroll', updateAvailableHeight, scrollOptions);
            document.removeEventListener('scroll', updateAvailableHeight, capturedScrollOptions);
            visualViewport?.removeEventListener('resize', updateAvailableHeight, scrollOptions);
            visualViewport?.removeEventListener('scroll', updateAvailableHeight, scrollOptions);
            resizeObserverRef.current?.disconnect();
        };
    }, [updateAvailableHeight]);

    return setElementRef;
}
